import React, { useContext, useEffect, useState } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { useIsFocused } from "@react-navigation/native";

import FormattedText from "../../../components/FormattedText";
import commonStyles from "../../../styles/commonStyles";
import OverflowTooltipText from "../../../components/OverflowTooltipText";
import { InvestmentModel } from "../../../types/IInvestmentTypes";
import { getUserIdToUse } from "../../../utils/getUserIdToUse";
import { ApiService } from "../../../service/ApiService";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { RefreshContext } from "../../../contexts/refreshContext";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../../components/AppText";

export interface InvestmentDetailsProps {
  navigation: {
    navigate: (screen: string, params?: { [key: string]: any }) => void;
    goBack: () => void;
  };
  route: {
    params: { item: InvestmentModel[]; source?: string };
  };
}
const CurrentQEMIDetails: React.FC<InvestmentDetailsProps> = ({
  navigation,
  route}) => {
  const investmentsData = route.params?.item || [];
  const source = route?.params?.source || "Unknown";
  const [investments, setInvestments] = useState<InvestmentModel[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  let userIdToUse: string | null;
  const isFocused = useIsFocused();
  const { refreshTrigger } = useContext(RefreshContext);

  useEffect(() => {
    if (source === "EMI") {
      fetchInvestmentData();
    } else if (source === "Investment" && route.params?.item) {
      const filteredItemData = filterValidInvestments(investmentsData);
      setInvestments(filteredItemData);

      setLoading(false); // No need to load from API
    }
  }, [source, route.params?.item, refreshTrigger, isFocused]);



  const filterValidInvestments = (data: InvestmentModel[]) => {
    return data.filter(
      (investment) =>
        investment.revisedQemi !== null && investment.revisedQemi !== 0,
    );
  };

  const fetchInvestmentData = async () => {
    setLoading(true);
    try {
      userIdToUse = await getUserIdToUse();
      const response = await ApiService.get<InvestmentModel>(
        `portal/partnerinvestment/${userIdToUse}`,
      );

      const data = Array.isArray(response.data) ? response.data : [];
      setLoading(false);

      const filteredInvestments = filterValidInvestments(data);


      setInvestments(filteredInvestments);
    } catch (error) {
      setError("Error fetching data");
      setLoading(false);
    }
  };
  const onRefresh = async () => {
    if (source === "EMI") {
      setRefreshing(true);
      await fetchInvestmentData();
      setRefreshing(false);
    }
  };
  const getTotalForColumn = (columnName: string): number => {
    let total = 0;
    for (const record of investments) {
      total += (record as unknown as Record<string, number>)[columnName] || 0;
    }
    return total;
  };

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.flex1, commonStyles.screenBottomGap]}>
      <View style={[commonStyles.container]}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={commonStyles.subHeaderContainer}
        >
          <AntDesign name="left" size={24} color="black" />
          <OverflowTooltipText
            testID="your-emis"
            text="Your EMIs"
            style={commonStyles.subHeader}
            onPress={() => navigation.goBack()}
          />
        </TouchableOpacity>
        <View
          style={[
            commonStyles.subContainer,
            commonStyles.p16,
            commonStyles.mb5,
            commonStyles.mt5,
          ]}
        >
          <View style={commonStyles.summaryItem}>
            <AppText testID="total-quarterly-emi" style={commonStyles.commonFont}>
              Total Quarterly EMI
            </AppText>
            <FormattedText
              testID="total-quarterly-emi-value"
              value={getTotalForColumn("revisedQemi")}
              type="currency"
              style={commonStyles.summaryAmount}
              loading={loading}
            />
          </View>
          <View style={commonStyles.summaryItem}>
            <AppText testID="totally-yearly-emi" style={commonStyles.commonFont}>
              Total Yearly EMI
            </AppText>
            <FormattedText
              testID="total-yearly-emi-value"
              value={getTotalForColumn("revisedQemi") * 4}
              type="currency"
              style={commonStyles.summaryAmount}
              loading={loading}
            />
          </View>
        </View>

        <AppText style={commonStyles.detailsHeader}>Details</AppText>

        {!loading ? (
          <ScrollView style={commonStyles.mt10}>
            <View
              style={[
                investments.length > 0 && commonStyles.subContainer,
                commonStyles.p14,
                commonStyles.mlar2,
                commonStyles.mt10,
                commonStyles.pt0,
                commonStyles.mb10,
              ]}
            >
              <View>
                {investments.map((data, index) => (
                  <View
                    key={index}
                    style={[
                      commonStyles.subDetails,
                      {
                        flexDirection: "column",
                        width: "100%",
                        paddingVertical: 12,
                        paddingHorizontal: 0},
                    ]}
                  >
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        justifyContent: "space-between"}}
                    >
                      <OverflowTooltipText
                        testID={`entity-name:${data.entityId}`}
                        text={data.entityName}
                        style={[
                          commonStyles.subTitle,
                          {
                            color: "black"},
                        ]}
                        containerWidth="65%"
                      />
                      <FormattedText
                        value={data.revisedQemi ?? 0}
                        type="currency"
                        style={commonStyles.subValue}
                      />
                    </View>
                    <View
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        gap: 8}}
                    >
                      <View
                        style={[
                          commonStyles.detailsGroupContainer,
                          {
                            flexDirection: "row",
                            paddingTop: 8,

                            marginRight: 7,
                            width: "35%"},
                        ]}
                      >
                        <AppText
                          style={commonStyles.detailsLabel}
                          testID={`due:${data.entityId}`}
                        >
                          Due:{" "}
                        </AppText>
                        <AppText
                          style={commonStyles.detailsValue}
                          testID={`due-date-value:${data.entityId}`}
                        >
                          {data.emiDueDate
                            ? data.emiDueDate.slice(0, 6) +
                              data.emiDueDate.slice(-2)
                            : "N/A"}
                        </AppText>
                      </View>
                      <View
                        style={[
                          commonStyles.detailsGroupContainer,
                          {
                            flexDirection: "row",
                            paddingTop: 8,

                            marginRight: 7,
                            width: "65%"},
                        ]}
                      >
                        <AppText
                          style={commonStyles.detailsLabel}
                          testID={`zelle:${data.entityId}`}
                        >
                          Zelle:{" "}
                        </AppText>
                        <OverflowTooltipText
                          testID={`zelle-value:${data.entityId}`}
                          style={commonStyles.detailsValue}
                          text={data.zelleId || "N/A"}
                          containerWidth="76%"
                        />
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        ) : (
          <View testID="skeleton-loader">
            <SkeletonLoader rows={10}></SkeletonLoader>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

export default CurrentQEMIDetails;
